import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatCurrency } from "@/lib/utils";
import { FileText, Scale, Banknote, Plane, PieChart as PieIcon } from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from "recharts";

const REVENUE_COLORS = ["#10b981", "#06b6d4", "#8b5cf6"];
const EXPENSE_COLORS = ["#ef4444", "#f97316", "#eab308", "#06b6d4", "#8b5cf6", "#ec4899", "#64748b"];

function CurrencyTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const val = payload.find(p => p.value > 0 || p.value < 0);
  return val ? (
    <div className="bg-white shadow-lg border rounded-lg px-3 py-2">
      <p className="text-sm font-semibold">{formatCurrency(val.value)}</p>
    </div>
  ) : null;
}

export default function CompanyTab({ game, team }) {
  const { t } = useTranslation();
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (team?.id) fetchFinancials();
  }, [team?.id]);

  const fetchFinancials = async () => {
    try {
      const response = await axios.get(`${API}/teams/${team.id}/financials`);
      setFinancials(response.data);
    } catch {
      // Financials not available yet
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
        <div className="h-96 bg-slate-200 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-tab">
      <div>
        <h2 className="text-2xl font-heading font-bold text-slate-900">{t("company.title")}</h2>
        <p className="text-slate-500">{t("history.quarter")} {game?.current_quarter} - {t("company.subtitle")}</p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 flex-wrap">
          <TabsTrigger value="overview" className="gap-2" data-testid="tab-overview">
            <PieIcon className="w-4 h-4" />
            {t("company.overview")}
          </TabsTrigger>
          <TabsTrigger value="income" className="gap-2" data-testid="tab-income-statement">
            <FileText className="w-4 h-4" />
            {t("company.incomeStatement")}
          </TabsTrigger>
          <TabsTrigger value="balance" className="gap-2" data-testid="tab-balance-sheet">
            <Scale className="w-4 h-4" />
            {t("company.balanceSheet")}
          </TabsTrigger>
          <TabsTrigger value="cashflow" className="gap-2" data-testid="tab-cash-flow">
            <Banknote className="w-4 h-4" />
            {t("company.cashFlow")}
          </TabsTrigger>
          <TabsTrigger value="fleet" className="gap-2" data-testid="tab-fleet-status">
            <Plane className="w-4 h-4" />
            {t("company.fleetStatus")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="tab-content">
          <FinancialOverview financials={financials} team={team} />
        </TabsContent>

        <TabsContent value="income" className="tab-content">
          <IncomeStatement data={financials?.income_statement} />
        </TabsContent>

        <TabsContent value="balance" className="tab-content">
          <BalanceSheet data={financials?.balance_sheet} />
        </TabsContent>

        <TabsContent value="cashflow" className="tab-content">
          <CashFlowStatement data={financials?.cash_flow} />
        </TabsContent>

        <TabsContent value="fleet" className="tab-content">
          <FleetStatus team={team} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FinancialOverview({ financials, team }) {
  const { t } = useTranslation();
  const income = financials?.income_statement;
  const balance = financials?.balance_sheet;
  const cashFlow = financials?.cash_flow;

  // Revenue breakdown
  const revenueData = [
    { name: t("company.passengerRevenue"), value: income?.revenue?.passenger || 0 },
    { name: t("company.cargoRevenue"), value: income?.revenue?.cargo || 0 },
  ].filter(d => d.value > 0);

  // Expense breakdown
  const expenseData = [
    { name: t("company.fuelExpense"), value: income?.expenses?.fuel || 0 },
    { name: t("company.laborExpense"), value: income?.expenses?.labor || 0 },
    { name: t("company.marketingExpense"), value: income?.expenses?.marketing || 0 },
    { name: t("company.maintenanceExpense"), value: income?.expenses?.maintenance || 0 },
    { name: "Operations", value: income?.expenses?.operations || 0 },
    { name: "Customer Experience", value: income?.expenses?.customer_experience || 0 },
    { name: "Lease & Depreciation", value: (income?.expenses?.lease_costs || 0) + (income?.expenses?.depreciation || 0) },
  ].filter(d => d.value > 0);

  // Profit margin
  const totalRevenue = income?.revenue?.total || 0;
  const totalExpenses = income?.expenses?.total || 0;
  const netProfit = income?.net_income || 0;
  const margin = totalRevenue > 0 ? (netProfit / totalRevenue * 100) : 0;

  // Summary bar data
  const summaryData = [
    { name: t("company.revenue"), revenue: totalRevenue, expenses: 0, profit: 0 },
    { name: t("company.expenses"), revenue: 0, expenses: totalExpenses, profit: 0 },
    { name: t("company.profit"), revenue: 0, expenses: 0, profit: netProfit },
  ];

  const hasData = totalRevenue > 0 || totalExpenses > 0;

  if (!hasData) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <PieIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">{t("company.noFinancialData")}</p>
          <p className="text-sm text-slate-400 mt-1">{t("company.dataAfterAdvance")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard label={t("company.totalRevenue")} value={formatCurrency(totalRevenue)} color="text-emerald-600" />
        <SummaryCard label={t("company.totalExpenses")} value={formatCurrency(totalExpenses)} color="text-rose-600" />
        <SummaryCard label={t("company.netProfit")} value={formatCurrency(netProfit)} color={netProfit >= 0 ? "text-emerald-600" : "text-rose-600"} />
        <SummaryCard label={t("company.profitMargin")} value={`${margin.toFixed(1)}%`} color={margin >= 0 ? "text-sky-600" : "text-rose-600"} />
      </div>

      {/* Revenue vs Expenses Bar */}
      <Card>
        <CardHeader className="card-header-flight-deck">
          <CardTitle>{t("company.revenueVsExpenses")}</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="h-48" data-testid="summary-bar-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summaryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" width={80} />
                <Tooltip content={<CurrencyTooltip />} />
                <Bar dataKey="revenue" fill="#10b981" radius={[0, 4, 4, 0]} />
                <Bar dataKey="expenses" fill="#f43f5e" radius={[0, 4, 4, 0]} />
                <Bar dataKey="profit" fill={netProfit >= 0 ? "#0ea5e9" : "#f43f5e"} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Donut Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {revenueData.length > 0 && (
          <Card>
            <CardHeader className="card-header-flight-deck">
              <CardTitle>{t("company.revenueBreakdown")}</CardTitle>
              <CardDescription>{t("company.revenueBreakdownDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="h-56" data-testid="revenue-donut">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={revenueData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={45} paddingAngle={3}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {revenueData.map((_, i) => <Cell key={i} fill={REVENUE_COLORS[i % REVENUE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {expenseData.length > 0 && (
          <Card>
            <CardHeader className="card-header-flight-deck">
              <CardTitle>{t("company.expenseBreakdown")}</CardTitle>
              <CardDescription>{t("company.expenseBreakdownDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="h-56" data-testid="expense-donut">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={expenseData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={45} paddingAngle={2}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {expenseData.map((_, i) => <Cell key={i} fill={EXPENSE_COLORS[i % EXPENSE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
        <p className={`text-xl font-mono font-bold mt-1 ${color}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function IncomeStatement({ data }) {
  const { t } = useTranslation();
  if (!data) return <EmptyCard message={t("company.noIncomeData")} />;
  const rev = data.revenue || {};
  const exp = data.expenses || {};
  const rows = [
    { label: t("company.revenue"), isHeader: true },
    { label: t("company.passengerRevenue"), value: rev.passenger },
    { label: t("company.cargoRevenue"), value: rev.cargo },
    { label: t("company.totalRevenue"), value: rev.total, isTotal: true },
    { spacer: true },
    { label: t("company.operatingExpenses"), isHeader: true },
    { label: t("company.fuelExpense"), value: exp.fuel, isExpense: true },
    { label: t("company.laborExpense"), value: exp.labor, isExpense: true },
    { label: t("company.marketingExpense"), value: exp.marketing, isExpense: true },
    { label: t("company.maintenanceExpense"), value: exp.maintenance, isExpense: true },
    { label: "Operations", value: exp.operations, isExpense: true },
    { label: "Customer Experience", value: exp.customer_experience, isExpense: true },
    { label: "Risk & Insurance", value: exp.risk_and_insurance, isExpense: true },
    { label: "Lease Costs", value: exp.lease_costs, isExpense: true },
    { label: "Depreciation", value: exp.depreciation, isExpense: true },
    { label: "Route Fees", value: exp.route_fees, isExpense: true },
    { label: t("company.strategicExpense"), value: exp.strategic, isExpense: true },
    { label: t("company.dynamicFareExpense"), value: exp.dynamic_fare_system, isExpense: true },
    ...(exp.event_costs ? [{ label: t("company.eventExpense"), value: exp.event_costs, isExpense: true }] : []),
    { label: t("company.totalExpenses"), value: exp.total, isTotal: true, isExpense: true },
    { spacer: true },
    { label: t("company.netOperatingIncome"), value: data.net_operating_income, isTotal: true },
    { label: t("company.interestExpense"), value: data.interest_expense, isExpense: true },
    { spacer: true },
    { label: t("company.netProfit"), value: data.net_income, isTotal: true, highlight: true },
  ];
  return <FinancialTable title={t("company.incomeStatement")} rows={rows} />;
}

function BalanceSheet({ data }) {
  const { t } = useTranslation();
  if (!data) return <EmptyCard message={t("company.noBalanceData")} />;
  const a = data.assets || {};
  const l = data.liabilities || {};
  const e = data.equity || {};
  const rows = [
    { label: t("company.totalAssets"), isHeader: true },
    { label: t("company.cashEquivalents"), value: a.cash },
    { label: t("company.aircraftValue"), value: a.aircraft_fleet_value },
    { label: "Spare Parts Inventory", value: a.spare_parts_inventory },
    { label: "Prepaid Expenses", value: a.prepaid_expenses },
    { label: "Airport Slot Deposits", value: a.airport_slot_deposits },
    { label: t("company.totalAssets"), value: a.total, isTotal: true },
    { spacer: true },
    { label: t("company.totalLiabilities"), isHeader: true },
    { label: t("company.shortTermDebt"), value: l.short_term_debt },
    ...(l.emergency_loan_balance ? [{ label: t("company.emergencyLoanBalance"), value: l.emergency_loan_balance }] : []),
    { label: t("company.longTermDebt"), value: l.long_term_debt },
    { label: t("company.totalLiabilities"), value: l.total, isTotal: true },
    { spacer: true },
    { label: t("company.shareholdersEquity"), isHeader: true },
    { label: "Paid-in Capital", value: e.paid_in_capital },
    { label: t("company.retainedEarnings"), value: e.retained_earnings },
    { label: t("company.sharesOutstanding"), value: e.shares_outstanding, formatAsNumber: true },
    { label: t("company.totalEquity"), value: e.total_equity, isTotal: true, highlight: true },
  ];
  return <FinancialTable title={t("company.balanceSheet")} rows={rows} />;
}

function CashFlowStatement({ data }) {
  const { t } = useTranslation();
  if (!data) return <EmptyCard message={t("company.noCashFlowData")} />;
  const rows = [
    { label: t("company.operatingActivities"), isHeader: true },
    { label: t("company.netOperatingIncome"), value: data.net_operating_income },
    { label: t("company.depreciationAddback"), value: data.depreciation_addback },
    ...(data.fleet_sale_result_removed ? [{ label: t("company.saleResultRemoved"), value: data.fleet_sale_result_removed }] : []),
    { label: t("company.operatingCashFlow"), value: data.operating, isTotal: true },
    { spacer: true },
    { label: t("company.investingActivities"), isHeader: true },
    { label: "Aircraft Purchases", value: typeof data.investing === "object" ? data.investing.fleet_purchases : undefined, isExpense: true },
    { label: "Aircraft Sales", value: typeof data.investing === "object" ? data.investing.fleet_sales : undefined },
    { label: t("company.investingCashFlow"), value: typeof data.investing === "object" ? data.investing.total : data.investing, isTotal: true },
    { spacer: true },
    { label: t("company.financingActivities"), isHeader: true },
    { label: "New Borrowing", value: data.financing?.new_borrowing, isExpense: true },
    { label: "Debt Repayment", value: data.financing?.debt_repayment, isExpense: true },
    { label: "Share Proceeds", value: data.financing?.share_proceeds, isExpense: true },
    { label: "Share Buyback", value: data.financing?.buyback_cost, isExpense: true },
    { label: "Dividends", value: data.financing?.dividends, isExpense: true },
    { label: "Interest", value: data.financing?.interest, isExpense: true },
    ...(data.financing?.emergency_loan ? [{ label: t("company.emergencyLoan"), value: data.financing.emergency_loan }] : []),
    { label: t("company.financingCashFlow"), value: data.financing?.total, isTotal: true },
    { spacer: true },
    { label: t("company.netChangeInCash"), value: data.net_change, isTotal: true, highlight: true },
    ...(data.closing_cash === undefined ? [] : [
      { spacer: true },
      { label: t("company.cashReconciliation"), isHeader: true },
      { label: t("company.openingCash"), value: data.opening_cash },
      { label: t("company.closingCash"), value: data.closing_cash },
      { label: t("company.actualNetChange"), value: data.actual_net_change },
      { label: t("company.unreconciled"), value: data.unreconciled, isTotal: true },
    ]),
  ];
  return <FinancialTable title={t("company.cashFlow")} rows={rows} />;
}

function FleetStatus({ team }) {
  const { t } = useTranslation();
  const aircraft = team?.aircraft || [];
  // Group aircraft by type
  const byType = aircraft.reduce((acc, ac) => {
    const type = ac.type || "unknown";
    if (!acc[type]) acc[type] = { count: 0, owned: 0, leased: 0 };
    acc[type].count++;
    if (ac.owned) acc[type].owned++;
    else acc[type].leased++;
    return acc;
  }, {});

  const typeNames = {
    turboprop: "Turboprop (ATR 72)",
    used_narrowbody: "Used Narrow-body (A320)",
    new_narrowbody: "New Narrow-body (737 MAX)",
    widebody: "Wide-body (787)",
  };

  return (
    <Card>
      <CardHeader className="card-header-flight-deck">
        <CardTitle>{t("company.fleetStatus")}</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">{t("company.totalAircraft")}</p>
            <p className="text-3xl font-mono font-bold text-slate-900">{team?.aircraft_count || 0}</p>
          </div>
          {Object.entries(byType).map(([type, info]) => (
            <div key={type} className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Plane className="w-5 h-5 text-sky-500" />
                <p className="font-semibold text-slate-800">{typeNames[type] || type}</p>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Count:</span>
                <span className="font-mono font-medium">{info.count}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Owned:</span>
                <span className="font-mono font-medium">{info.owned}</span>
              </div>
              {info.leased > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Leased:</span>
                  <span className="font-mono font-medium">{info.leased}</span>
                </div>
              )}
            </div>
          ))}
          {aircraft.length === 0 && (
            <div className="p-4 bg-slate-50 rounded-lg text-center text-slate-500 col-span-2">
              No aircraft data available
            </div>
          )}
        </div>

        {/* Individual Aircraft Details */}
        {aircraft.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-semibold text-slate-600 mb-3">Aircraft Details</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[500px]">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">ID</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Type</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500">Age</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500">Condition</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500">Ownership</th>
                  </tr>
                </thead>
                <tbody>
                  {aircraft.map((ac) => (
                    <tr key={ac.id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="py-2 px-3 font-mono text-xs">{ac.id}</td>
                      <td className="py-2 px-3">{typeNames[ac.type] || ac.type}</td>
                      <td className="py-2 px-3 text-center font-mono">{Number(ac.age).toFixed(1)} yrs</td>
                      <td className="py-2 px-3 text-center">
                        <span className={`font-mono ${ac.condition >= 80 ? "text-emerald-600" : ac.condition >= 60 ? "text-amber-600" : "text-rose-600"}`}>
                          {ac.condition}%
                        </span>
                      </td>
                      <td className="py-2 px-3 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs ${ac.owned ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>
                          {ac.owned ? "Owned" : "Leased"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FinancialTable({ title, rows }) {
  return (
    <Card>
      <CardHeader className="card-header-flight-deck">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <table className="financial-table w-full" data-testid={`${title.toLowerCase().replace(/ /g, '-')}-table`}>
          <tbody>
            {rows.map((row, i) => <FinancialRow key={row.label || `spacer-${i}`} {...row} />)}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function FinancialRow({ label, value, isHeader, isTotal, isExpense, highlight, spacer, formatAsNumber }) {
  if (spacer) return <tr className="h-4"><td colSpan={2} /></tr>;
  if (isHeader) return (
    <tr className="financial-row-header">
      <td colSpan={2} className="py-3 px-4 font-semibold text-slate-700 bg-slate-50">{label}</td>
    </tr>
  );
  const valueClass = value < 0 ? "text-rose-600" : (highlight && value > 0 ? "text-emerald-600" : "");
  const displayValue = formatAsNumber ? (value || 0).toLocaleString() : formatCurrency(value || 0);
  return (
    <tr className={highlight ? "bg-slate-50" : ""}>
      <td className={`py-2 px-4 ${isTotal ? "font-semibold" : ""} ${isExpense ? "pl-8" : ""}`}>{label}</td>
      <td className={`py-2 px-4 text-right font-mono ${isTotal ? "font-semibold" : ""} ${valueClass}`}>
        {displayValue}
      </td>
    </tr>
  );
}

function EmptyCard({ message }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">{message}</p>
        <p className="text-sm text-slate-400 mt-1">{t("company.dataAfterAdvance")}</p>
      </CardContent>
    </Card>
  );
}
